import { Session, SignalEvent } from 'openvidu-browser-v2compatibility';
import { IChatAttachment, IChatMessage } from '../../shared/interfaces';
import { Roles, Signals } from '../../shared/enums';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { uploadChatAttachment } from '../../services/chatAttachmentService';
import { convertPdfToImageFile, isPdfFile } from '../../services/pdfToImageService';

import { Button } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import './ChatMessageView.css';

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

interface AttachmentSignalPayload {
  kind: 'attachment';
  version: 1 | 2;
  id: string;
  origin: Roles;
  fileName: string;
  contentType: string;
  size: number;
  previewDataUrl?: string;
  url?: string;
  storageKey?: string;
  expiresAt?: string;
}

interface AttachmentChunkStartSignalPayload {
  kind: 'attachment-chunk-start';
  version: 1;
  id: string;
  origin: Roles;
  fileName: string;
  contentType: string;
  size: number;
  totalChunks: number;
}

interface AttachmentChunkSignalPayload {
  kind: 'attachment-chunk';
  version: 1;
  id: string;
  chunkIndex: number;
  data: string;
}

interface AttachmentChunkEndSignalPayload {
  kind: 'attachment-chunk-end';
  version: 1;
  id: string;
}

type ChatAttachmentSignalPayload =
  | AttachmentSignalPayload
  | AttachmentChunkStartSignalPayload
  | AttachmentChunkSignalPayload
  | AttachmentChunkEndSignalPayload;

interface PendingAttachmentChunks {
  origin: Roles;
  read: boolean;
  date: Date | number;
  fileName: string;
  contentType: string;
  size: number;
  totalChunks: number;
  chunks: string[];
  receivedChunks: number;
}

export default function ChatMessageView(props: {
  setUnreadMessages: Function;
  isActiveChat: boolean;
  session: Session;
}) {
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingAttachmentChunksRef = useRef<Map<string, PendingAttachmentChunks>>(new Map());
  const [attachmentError, setAttachmentError] = useState('');
  const [input, setInput] = useState('');
  const [lightboxAttachment, setLightboxAttachment] = useState<IChatAttachment | null>(null);

  function sendMessage() {
    if (!input.trim()) return;

    const messageFromInsured: IChatMessage = {
      read: false,
      kind: 'text',
      text: input,
      date: new Date(),
      origin: Roles.Insured,
    };

    props.session.signal({
      data: messageFromInsured.text,
      type: Signals.MessagingAnalyst,
    });

    setMessages((messages) => [...messages, messageFromInsured]);
    setInput('');
  }

  async function sendAttachment(originalFile: File) {
    setAttachmentError('');

    let file = originalFile;

    if (isPdfFile(originalFile)) {
      if (originalFile.size > MAX_ATTACHMENT_SIZE_BYTES) {
        setAttachmentError('Arquivo maior que 10 MB.');
        return;
      }

      try {
        file = await convertPdfToImageFile(originalFile);
      } catch (error) {
        console.error('[ChatAttachment] failed to convert pdf', {
          fileName: originalFile.name,
          fileSize: originalFile.size,
          error,
        });
        setAttachmentError('Nao foi possivel converter o PDF.');
        return;
      }
    }

    const validationError = validateAttachment(file);
    if (validationError) {
      console.warn('[ChatAttachment] validation failed', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        validationError,
      });
      setAttachmentError(validationError);
      return;
    }

    if (!props.session.isSessionConnected()) {
      console.warn('[ChatAttachment] session is not connected', {
        fileName: file.name,
        sessionId: props.session.sessionId,
      });
      setAttachmentError('A sessao nao esta ativa.');
      return;
    }

    let payloadBytes = 0;

    try {
      const uploadedAttachment = await uploadChatAttachment(props.session.sessionId, file);
      const attachment: IChatAttachment = {
        id: uploadedAttachment.id,
        fileName: uploadedAttachment.fileName,
        contentType: uploadedAttachment.contentType,
        size: uploadedAttachment.size,
        url: uploadedAttachment.url,
      };
      const messageFromInsured: IChatMessage = {
        read: false,
        kind: 'attachment',
        date: new Date(),
        origin: Roles.Insured,
        attachment,
      };
      const attachmentPayload: AttachmentSignalPayload = {
        kind: 'attachment',
        version: 2,
        id: attachment.id,
        origin: Roles.Insured,
        fileName: attachment.fileName,
        contentType: attachment.contentType,
        size: attachment.size,
        url: attachment.url,
      };
      payloadBytes = new TextEncoder().encode(JSON.stringify(attachmentPayload)).length;

      console.info('[ChatAttachment] sending attachment', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        payloadBytes,
        signalType: Signals.MessagingAnalyst,
        sessionId: props.session.sessionId,
        isConnected: props.session.isSessionConnected(),
      });

      await sendChatAttachmentSignal(attachmentPayload, Signals.MessagingAnalyst);

      console.info('[ChatAttachment] attachment signal sent', {
        attachmentId: attachment.id,
        payloadBytes,
      });
      setMessages((messages) => [...messages, messageFromInsured]);
    } catch (error) {
      console.error('[ChatAttachment] failed to send attachment', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        payloadBytes,
        sessionId: props.session.sessionId,
        isConnected: props.session.isSessionConnected(),
        error,
      });
      setAttachmentError('Nao foi possivel enviar o anexo.');
    }
  }

  async function sendChatAttachmentSignal(payload: ChatAttachmentSignalPayload, signalType: Signals) {
    await props.session.signal({
      data: JSON.stringify(payload),
      type: signalType,
    });
  }

  function validateAttachment(file: File) {
    const fileName = file.name.toLowerCase();
    const isAllowedImage =
      ALLOWED_ATTACHMENT_TYPES.includes(file.type) ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png');

    if (!isAllowedImage) {
      return 'Formato invalido. Envie JPG ou PNG.';
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      return 'Arquivo maior que 10 MB.';
    }

    return '';
  }

  function onAttachmentClick() {
    fileInputRef.current?.click();
  }

  function onAttachmentSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (file) {
      sendAttachment(file);
    }
  }

  function parseSignalMessage(event: SignalEvent, origin: Roles): IChatMessage | null {
    const data = event.data || '';
    const messageTime = event.from?.creationTime || new Date();

    try {
      const payload = JSON.parse(data) as ChatAttachmentSignalPayload;

      if (payload.kind === 'attachment' && (payload.previewDataUrl || payload.url)) {
        return {
          read: props.isActiveChat,
          origin,
          kind: 'attachment',
          attachment: {
            id: payload.id,
            fileName: payload.fileName,
            contentType: payload.contentType,
            size: payload.size,
            previewDataUrl: payload.previewDataUrl,
            url: payload.url,
            storageKey: payload.storageKey,
            expiresAt: payload.expiresAt,
          },
          date: messageTime,
        };
      }

      if (payload.kind === 'attachment-chunk-start') {
        pendingAttachmentChunksRef.current.set(payload.id, {
          origin,
          read: props.isActiveChat,
          date: messageTime,
          fileName: payload.fileName,
          contentType: payload.contentType,
          size: payload.size,
          totalChunks: payload.totalChunks,
          chunks: [],
          receivedChunks: 0,
        });

        console.info('[ChatAttachment] receiving attachment chunks', {
          attachmentId: payload.id,
          fileName: payload.fileName,
          totalChunks: payload.totalChunks,
          origin,
        });

        return null;
      }

      if (payload.kind === 'attachment-chunk') {
        const pendingAttachment = pendingAttachmentChunksRef.current.get(payload.id);

        if (!pendingAttachment) {
          console.warn('[ChatAttachment] received chunk without start', {
            attachmentId: payload.id,
            chunkIndex: payload.chunkIndex,
          });
          return null;
        }

        if (!pendingAttachment.chunks[payload.chunkIndex]) {
          pendingAttachment.chunks[payload.chunkIndex] = payload.data;
          pendingAttachment.receivedChunks += 1;
        }

        return null;
      }

      if (payload.kind === 'attachment-chunk-end') {
        const pendingAttachment = pendingAttachmentChunksRef.current.get(payload.id);

        if (!pendingAttachment) {
          console.warn('[ChatAttachment] received chunk end without start', {
            attachmentId: payload.id,
          });
          return null;
        }

        if (pendingAttachment.receivedChunks !== pendingAttachment.totalChunks) {
          console.warn('[ChatAttachment] attachment chunks incomplete', {
            attachmentId: payload.id,
            receivedChunks: pendingAttachment.receivedChunks,
            totalChunks: pendingAttachment.totalChunks,
          });
          return null;
        }

        pendingAttachmentChunksRef.current.delete(payload.id);
        const previewDataUrl = pendingAttachment.chunks.join('');

        console.info('[ChatAttachment] attachment chunks received', {
          attachmentId: payload.id,
          totalChunks: pendingAttachment.totalChunks,
          previewDataUrlLength: previewDataUrl.length,
        });

        return {
          read: pendingAttachment.read,
          origin: pendingAttachment.origin,
          kind: 'attachment',
          attachment: {
            id: payload.id,
            fileName: pendingAttachment.fileName,
            contentType: pendingAttachment.contentType,
            size: pendingAttachment.size,
            previewDataUrl,
          },
          date: pendingAttachment.date,
        };
      }
    } catch {
      // Keep compatibility with plain text chat payloads.
    }

    return {
      read: props.isActiveChat,
      origin,
      kind: 'text',
      text: data,
      date: messageTime,
    };
  }

  function formatAttachmentSize(size: number) {
    return `${Math.max(size / 1024, 1).toFixed(0)} KB`;
  }

  function renderMessage(message: IChatMessage, key: number) {
    const className = `messageText ${message.origin === Roles.Insured ? 'insuredMessage' : 'analystMessage'}`;

    if (message.kind === 'attachment' && message.attachment) {
      const attachmentUrl = message.attachment.url || message.attachment.previewDataUrl;

      return (
        <div className={className} key={key} ref={messagesEndRef}>
          <div className="attachmentCard">
            {attachmentUrl ? (
              <button
                type="button"
                className="attachmentThumbButton"
                onClick={() => setLightboxAttachment(message.attachment!)}
                title="Abrir anexo"
              >
                <img className="attachmentThumb" src={attachmentUrl} alt={message.attachment.fileName} />
              </button>
            ) : <></>}
            <div className="attachmentInfo">
              <div className="attachmentName">{message.attachment.fileName}</div>
              <div className="attachmentMeta">{formatAttachmentSize(message.attachment.size)}</div>
              {attachmentUrl ? (
                <a
                  className="attachmentDownload"
                  href={attachmentUrl}
                  download={message.attachment.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Baixar
                </a>
              ) : <></>}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={className} key={key} ref={messagesEndRef}>
        {message.text}
      </div>
    );
  }

  function onInputKeyPress(e: FormEvent<HTMLTextAreaElement>) {
    setInput((e.target as HTMLTextAreaElement).value);
  }

  function scrollIntoView() {
    if (messagesEndRef.current) {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  useEffect(() => {
    scrollIntoView();
  }, [messages]);

  useEffect(() => {
    if (!lightboxAttachment) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setLightboxAttachment(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxAttachment]);

  useEffect(() => {
    if (props.isActiveChat) {
      props.setUnreadMessages(false);
    }
  }, [props.isActiveChat]);

  useEffect(() => {
    messages.find((i) => i.origin === Roles.Analyst && i.read === false)
      ? props.setUnreadMessages(true)
      : props.setUnreadMessages(false);
  }, [messages]);

  useEffect(() => {
    props.session.on(`signal:${Signals.MessagingInsured}`, (event: SignalEvent) => {
      const messageFromAnalyst = parseSignalMessage(event, Roles.Analyst);

      if (!messageFromAnalyst) return;

      setMessages((messages) => [...messages, messageFromAnalyst]);
    });
  }, []);

  return (
    <div className="messageContainer">
      {lightboxAttachment ? (
        <div
          className="attachmentLightbox"
          onClick={() => setLightboxAttachment(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="attachmentLightboxClose"
            onClick={() => setLightboxAttachment(null)}
            aria-label="Fechar"
          >
            ×
          </button>
          <img
            className="attachmentLightboxImage"
            src={lightboxAttachment.url || lightboxAttachment.previewDataUrl}
            alt={lightboxAttachment.fileName}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : <></>}
      {props.isActiveChat ? (
        <div className="messageBox">
          <div className="messageContent">
            {messages.map(renderMessage)}
          </div>
          <div className="messageControl">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              hidden
              onChange={onAttachmentSelected}
            />
            <textarea typeof="text" value={input} onInput={onInputKeyPress} />
            <Button
              title="Anexar arquivo"
              onClick={onAttachmentClick}
              sx={{
                minWidth: '42px',
                width: '42px',
                flexShrink: 0,
                margin: '0',
                height: '42px',
                color: 'white',
                padding: '8px',
                borderRadius: '8px',
                background: '#555',
              }}
            >
              <AttachFile fontSize="small" />
            </Button>
            <Button
              disabled={!props.isActiveChat}
              // placeholder={'Enviar'}
              onClick={sendMessage}
              sx={{
                gap: '10px',
                width: '66px',
                flexShrink: 0,
                margin: '0',
                height: '42px',
                color: 'white',
                padding: '15px',
                borderRadius: '8px',
                position: 'relative',
                background: '#FF8527',
              }}
            >
              Enviar
            </Button>
          </div>
          {attachmentError ? <div className="attachmentError">{attachmentError}</div> : <></>}
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
